import React from 'react';
import { Link } from 'react-router-dom';

const AccountDeletionPage = () => {
  return (
    <div className="min-h-screen bg-gray-50" data-testid="account-deletion-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-gray-900 to-gray-800 text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <h1 className="text-2xl font-bold">
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text">Site</span>
                <span className="text-white">Orbit</span>
              </h1>
            </Link>
            <Link 
              to="/" 
              className="text-gray-300 hover:text-white flex items-center gap-2 text-sm font-medium transition-colors"
              data-testid="back-to-home-link"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Page Title */}
      <div className="bg-gradient-to-b from-gray-100 to-gray-50 py-12 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Account & Data Deletion</h1>
          <p className="text-gray-600">How to request deletion of your SiteOrbit account and associated data</p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 md:p-12">

          <p className="text-gray-600 leading-relaxed mb-8">
            At SiteOrbit, we respect your right to control your personal data. If you wish to delete your account and all associated data, please follow the steps below.
          </p>

          {/* Steps */}
          <h2 className="text-xl font-semibold text-gray-800 mb-6">How to Request Account Deletion</h2>

          <div className="space-y-6 mb-10">
            {/* Step 1 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 text-yellow-700 font-bold flex items-center justify-center text-sm">
                1
              </div>
              <div>
                <h3 className="font-medium text-gray-800 mb-1">Send an email to our support team</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Send an email to{' '}
                  <a 
                    href="mailto:support@webmail.siteorbit.app?subject=Account%20Deletion%20Request"
                    className="text-yellow-600 hover:text-yellow-700 font-medium"
                    data-testid="support-email-link"
                  >
                    support@webmail.siteorbit.app
                  </a>
                  {' '}with the subject line <strong>"Account Deletion Request"</strong>.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 text-yellow-700 font-bold flex items-center justify-center text-sm">
                2
              </div>
              <div>
                <h3 className="font-medium text-gray-800 mb-1">Include your account details</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  In your email, please include the <strong>full name</strong> and <strong>email address</strong> associated with your SiteOrbit account so we can locate and verify your identity.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 text-yellow-700 font-bold flex items-center justify-center text-sm">
                3
              </div>
              <div>
                <h3 className="font-medium text-gray-800 mb-1">Wait for confirmation</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Our support team will process your request and permanently delete your account and all associated data within <strong>3–5 working days</strong>. You will receive a confirmation email once the deletion is complete.
                </p>
              </div>
            </div>
          </div>

          {/* Partial Deletion */}
          <div className="border-t border-gray-200 pt-8 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Partial Data Deletion</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              If you do not wish to delete your entire account, you can choose to delete specific data instead.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Some data can be deleted directly by you within the app. For any data that you are unable to delete yourself, you can request partial deletion by emailing{' '}
              <a 
                href="mailto:support@webmail.siteorbit.app?subject=Partial%20Data%20Deletion%20Request"
                className="text-yellow-600 hover:text-yellow-700 font-medium"
                data-testid="partial-deletion-email-link"
              >
                support@webmail.siteorbit.app
              </a>
              {' '}with the subject line <strong>"Partial Data Deletion Request"</strong>.
            </p>
            <p className="text-gray-600 leading-relaxed">
              In your email, please specify which data you would like removed and include the <strong>full name</strong> and <strong>email address</strong> associated with your account. Our support team will process your request within <strong>3–5 working days</strong>.
            </p>
          </div>

          {/* Full Account Deletion - What gets deleted */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">What data is deleted with full account deletion?</h2>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Your user profile and account credentials
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Attendance and clock-in records
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Task assignments and activity history
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Uploaded photos and documents
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Any other personal data stored within the platform
              </li>
            </ul>
          </div>

          {/* Important note */}
          <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-6">
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="font-medium text-yellow-800 mb-1">Please note</h3>
                <p className="text-yellow-700 text-sm leading-relaxed">
                  Full account deletion is permanent and cannot be reversed. Once your data has been deleted, it cannot be recovered. If you are part of an organisation, please inform your administrator before requesting deletion.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p className="mb-4">Related Documents:</p>
          <div className="flex justify-center gap-6">
            <Link to="/privacy-policy" className="text-yellow-600 hover:text-yellow-700 font-medium">
              Privacy Policy
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/terms-of-service" className="text-yellow-600 hover:text-yellow-700 font-medium">
              Terms of Service
            </Link>
            <span className="text-gray-300">|</span>
            <Link to="/" className="text-yellow-600 hover:text-yellow-700 font-medium">
              Home
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} SiteOrbit. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AccountDeletionPage;
